import React from 'react';
import { motion } from 'motion/react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Shield, FileText, UserCheck, Globe, AlertTriangle, Scale, Bot, Users } from 'lucide-react';
import { AnimatedBackground } from '../AnimatedBackground';

export function TermsOfService() {
  const sections = [
    {
      icon: <FileText className="text-[#00BFFF]" size={32} />,
      title: '1. Acceptance of These Terms',
      content: [
        'These Terms of Service ("Terms") govern your access to and use of EdgeAI Academy and any related websites, services, courses, labs, study groups, chat features, and applications (collectively, the "Services") provided by MindEngage Inc. ("MindEngage", "we", "us", or "our").',
        'By creating an account, accessing, or using the Services, you agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you may not use the Services.',
      ],
    },
    {
      icon: <UserCheck className="text-[#FFD93D]" size={32} />,
      title: '2. Eligibility & Intended Audience',
      content: [
        'EdgeAI Academy is designed for adult learners, university students, and professionals interested in Edge AI, embedded systems, and IoT.',
        '• You must be at least 16 years old to use the Services.',
        '• If you are between 16 and 18 (or the age of majority in your jurisdiction), you may use the Services only with the consent and supervision of a parent, guardian, or authorized institution.',
        '• By using the Services, you represent that you have the legal capacity to enter into these Terms and, if you are using the Services on behalf of an organization, that you are authorized to bind that organization to these Terms.',
      ],
    },
    {
      icon: <Shield className="text-[#8B5CF6]" size={32} />,
      title: '3. Accounts, Security & Registration',
      content: [
        'To access many features of EdgeAI Academy, you must create an account or be provisioned an account through your organization.',
        '• You agree to provide accurate, current, and complete registration information and to update it as needed.',
        '• You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account.',
        '• You agree to notify us immediately of any unauthorized use of your account or any other breach of security.',
        '• We reserve the right to suspend or terminate accounts that are inactive, compromised, or used in violation of these Terms.',
      ],
    },
    {
      icon: <Globe className="text-[#00BFFF]" size={32} />,
      title: '4. License & Use of the Services',
      content: [
        'Subject to your compliance with these Terms, MindEngage grants you a limited, non-exclusive, non-transferable, revocable license to access and use the Services for your personal, educational, or internal professional purposes.',
        'You agree not to:',
        '• Use the Services for any illegal, harmful, or fraudulent activity.',
        '• Attempt to copy, modify, reverse engineer, or create derivative works of any part of the Services except as permitted by law.',
        '• Use automated tools (e.g., bots, scrapers) to access or extract data from the Services without our prior written permission.',
        '• Circumvent or attempt to circumvent any security, usage, or access limits associated with the Services.',
      ],
    },
    {
      icon: <Users className="text-[#FF6F61]" size={32} />,
      title: '5. Study Groups, Chat & User Content',
      content: [
        'EdgeAI Academy includes collaboration features such as study groups, chat, and shared workspaces where you and other users may exchange messages, files, and code ("User Content").',
        '• You are solely responsible for the User Content you share and for ensuring that it does not violate any laws, confidentiality obligations, or third-party rights.',
        '• You agree not to post User Content that is unlawful, harassing, discriminatory, hateful, defamatory, obscene, sexually explicit, or otherwise inappropriate.',
        '• You agree not to share sensitive personal information (such as government ID numbers, payment card data, or health records) in study groups or public channels.',
        '• By uploading or sharing User Content, you grant MindEngage a worldwide, non-exclusive, royalty-free license to host, store, display, and process that content solely for the purpose of operating, securing, and improving the Services.',
        'We may remove or restrict access to User Content that we believe violates these Terms, the law, or the rights or safety of others.',
      ],
    },
    {
      icon: <Bot className="text-[#00BFFF]" size={32} />,
      title: '6. AI Features & Limitations',
      content: [
        'The Services may include AI-powered assistants, code helpers, and other generative features.',
        '• AI outputs may be inaccurate, incomplete, or out of date. You should independently verify critical information before relying on it.',
        '• AI assistance is provided for educational and productivity purposes only and is not a substitute for professional advice (including legal, medical, financial, safety, or engineering certification advice).',
        '• You are responsible for how you use AI-generated content, including ensuring compliance with laws, regulations, and your organization’s policies.',
        '• You must not use AI features to generate malware, exploit code, or content intended to harm systems, individuals, or organizations.',
      ],
    },
    {
      icon: <FileText className="text-[#FFD93D]" size={32} />,
      title: '7. Courses, Labs & Intellectual Property',
      content: [
        'All course materials, labs, code samples, documentation, designs, and other content provided through EdgeAI Academy (excluding your own User Content) are owned by MindEngage or our licensors and are protected by copyright and other intellectual property laws.',
        '• You may use course and lab materials solely for your personal learning or internal professional development.',
        '• You may not sell, redistribute, publicly post, or commercially exploit course content without our express written permission.',
        '• Some courses may include open-source components subject to separate licenses. In those cases, the relevant open-source license terms will govern your use of those components.',
      ],
    },
    {
      icon: <Scale className="text-[#8B5CF6]" size={32} />,
      title: '8. Payments, Subscriptions & Trials',
      content: [
        'Certain features or courses may require paid subscriptions, one-time fees, or institution-level licensing.',
        '• Pricing, billing cycles, and applicable taxes will be disclosed at the time of purchase or enrollment.',
        '• Unless stated otherwise, subscriptions renew automatically at the end of each billing period until cancelled.',
        '• You may cancel your subscription in accordance with the instructions provided in your account or purchase flow. Cancellations typically take effect at the end of the current billing cycle.',
        '• Refunds, if offered, will be governed by our then-current refund policy or applicable consumer protection laws.',
        'If your access is provided by an organization, your payments and entitlements may be governed by a separate agreement between MindEngage and that organization.',
      ],
    },
    {
      icon: <Globe className="text-[#00BFFF]" size={32} />,
      title: '9. Third-Party Services & Links',
      content: [
        'The Services may integrate or interoperate with third-party products, services, and content (including cloud providers, communication tools, and AI APIs).',
        '• Your use of third-party services may be subject to separate terms and privacy policies.',
        '• MindEngage is not responsible for the content, functionality, or security of third-party services that are not under our control.',
        '• We may add, modify, or remove integrations at any time.',
      ],
    },
    {
      icon: <Shield className="text-[#8B5CF6]" size={32} />,
      title: '10. Acceptable Use & Prohibited Activities',
      content: [
        'In addition to other obligations described in these Terms, you agree not to:',
        '• Use the Services to transmit spam, phishing messages, or unsolicited commercial communication.',
        '• Upload or distribute malware, viruses, or other harmful code.',
        '• Interfere with or disrupt the integrity or performance of the Services or their underlying infrastructure.',
        '• Attempt to gain unauthorized access to accounts, systems, or networks.',
        '• Misrepresent your identity or affiliation, or impersonate another person.',
        '• Use the Services in connection with high-risk activities where failure or inaccuracy could lead to death, personal injury, or significant property or environmental damage.',
      ],
    },
    {
      icon: <AlertTriangle className="text-[#FF6F61]" size={32} />,
      title: '11. Disclaimers',
      content: [
        'To the fullest extent permitted by law, the Services are provided "as is" and "as available" without warranties of any kind, whether express, implied, or statutory.',
        'MindEngage specifically disclaims any implied warranties of merchantability, fitness for a particular purpose, non-infringement, and any warranties arising out of course of dealing or usage of trade.',
        'We do not warrant that the Services will be uninterrupted, secure, or error-free, or that any defects will be corrected.',
        'Course outcomes, certifications, or career results are not guaranteed. You are responsible for applying your knowledge appropriately and complying with applicable regulations and standards.',
      ],
    },
    {
      icon: <Scale className="text-[#8B5CF6]" size={32} />,
      title: '12. Limitation of Liability',
      content: [
        'To the maximum extent permitted by law, MindEngage and its officers, directors, employees, contractors, and affiliates will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits, revenues, data, or goodwill arising out of or related to your use of the Services.',
        'In all cases, our aggregate liability for any claims relating to the Services will be limited to the greater of (a) the amount you have paid to MindEngage for access to the Services in the twelve (12) months preceding the event giving rise to the claim, or (b) one hundred U.S. dollars (USD $100).',
        'Some jurisdictions do not allow the limitation of certain damages, so some of the above limitations may not apply to you.',
      ],
    },
    {
      icon: <Users className="text-[#FF6F61]" size={32} />,
      title: '13. Indemnification',
      content: [
        'You agree to indemnify, defend, and hold harmless MindEngage and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys’ fees) arising out of or related to:',
        '• Your use of the Services;',
        '• Your User Content;',
        '• Your violation of these Terms or applicable law; or',
        '• Any claim that your use of the Services or User Content infringes or misappropriates the rights of a third party.',
      ],
    },
    {
      icon: <FileText className="text-[#00BFFF]" size={32} />,
      title: '14. Termination & Suspension',
      content: [
        'We may suspend or terminate your access to the Services at any time, with or without notice, if we reasonably believe that:',
        '• You have violated these Terms or applicable law;',
        '• Your account has been compromised or used in a harmful way; or',
        '• Continued provision of the Services to you is no longer commercially or technically feasible.',
        'You may stop using the Services at any time and may request closure of your account, subject to any organizational or contractual requirements.',
        'Upon termination, your right to use the Services will cease, but sections of these Terms that by their nature should survive (such as intellectual property, disclaimers, limitations of liability, and indemnification) will continue to apply.',
      ],
    },
    {
      icon: <Globe className="text-[#00BFFF]" size={32} />,
      title: '15. Governing Law & Dispute Resolution',
      content: [
        'These Terms and any dispute arising out of or relating to them or the Services will be governed by the laws of the State of California, USA, without regard to its conflict of laws principles.',
        'You agree that any disputes will be resolved in the state or federal courts located in Santa Clara County, California, and you consent to the personal jurisdiction of such courts.',
        'Nothing in this section limits any mandatory rights you may have under the laws of your country of residence.',
      ],
    },
    {
      icon: <FileText className="text-[#FFD93D]" size={32} />,
      title: '16. Changes to These Terms',
      content: [
        'We may update these Terms from time to time to reflect changes in our Services, business practices, or legal requirements.',
        '• When we make material changes, we will provide notice through the Services, by email, or by other reasonable means.',
        '• The "Last updated" date below will be updated to reflect the most recent version.',
        '• Your continued use of the Services after the updated Terms become effective constitutes your acceptance of the changes.',
        'Last updated: November 19, 2025',
      ],
    },
  ];

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 relative text-slate-100">
      <AnimatedBackground variant="dimmed" />

      <div className="max-w-5xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#00BFFF] to-[#8B5CF6] mb-6 shadow-[0_0_40px_rgba(0,191,255,0.6)]">
            <Scale className="text-white" size={40} />
          </div>
          <h1 className="text-5xl md:text-6xl text-white mb-4">Terms of Service</h1>
          <p className="text-xl text-cyan-100/90 max-w-3xl mx-auto">
            Please read these terms carefully before using EdgeAI Academy.
          </p>
        </motion.div>

        {/* Intro / Non-legal disclaimer */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <Card className="bg-[#050915]/95 backdrop-blur-xl border border-cyan-400/40 shadow-[0_0_40px_rgba(0,229,209,0.25)]">
            <CardContent className="pt-6 text-slate-100/90 space-y-4 text-sm md:text-base">
              <p>
                These Terms of Service are intended as a general template for EdgeAI Academy and
                may need to be adapted to your specific business, jurisdiction, and organizational
                requirements. They do not constitute legal advice.
              </p>
              <p>
                We recommend that MindEngage Inc. and any institutional partners review these Terms
                with qualified legal counsel before relying on them in production.
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Sections */}
        {sections.map((section, index) => (
          <motion.div
            key={section.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.04 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <Card className="bg-[#050915]/95 backdrop-blur-xl border border-cyan-400/20 hover:border-cyan-400/40 transition-colors shadow-[0_0_30px_rgba(0,0,0,0.6)]">
              <CardHeader>
                <CardTitle className="text-2xl text-cyan-100 flex items-center gap-3">
                  {section.icon}
                  {section.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-slate-200/90 space-y-3 text-sm md:text-base">
                {section.content.map((paragraph, pIndex) => (
                  <p
                    key={pIndex}
                    className={paragraph.startsWith('•') ? 'ml-4' : ''}
                  >
                    {paragraph}
                  </p>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        ))}

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mt-12"
        >
          <Card className="bg-gradient-to-br from-[#00BFFF]/15 to-[#8B5CF6]/15 backdrop-blur-xl border border-cyan-400/60 shadow-[0_0_40px_rgba(0,191,255,0.4)]">
            <CardHeader>
              <CardTitle className="text-2xl text-cyan-100 flex items-center gap-3">
                <Shield className="text-[#00BFFF]" size={32} />
                Questions About These Terms
              </CardTitle>
            </CardHeader>
            <CardContent className="text-slate-100/90 space-y-2 text-sm md:text-base">
              <p>
                If you have questions about these Terms or how they apply to you or your
                organization, please contact us:
              </p>
              <div className="mt-4 space-y-1">
                <p><strong>MindEngage Inc</strong></p>
                <p>84 W. Santa Clara St., #700</p>
                <p>San Jose, CA 95113</p>
                <p className="mt-3">
                  <strong>Phone:</strong>{' '}
                  <a href="tel:+18669536888" className="text-[#00BFFF] hover:underline">
                    1 866 953 6888
                  </a>
                </p>
                <p>
                  <strong>Email:</strong>{' '}
                  <a
                    href="mailto:legal@mindengage.ai"
                    className="text-[#00BFFF] hover:underline"
                  >
                    legal@mindengage.ai
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
